import FieldNames from '../defaults/FieldNames.json';
import ErrorService from '../services/ErrorService';
import FeatureService from '../services/FeatureService';
import FundingService from '../services/FundingService';
import { removeCommas } from './FormatHelper';
// import { getSaveableDate } from './DateHelper';

export function ResetFeatureFundingLinesByFeature(feature, userInfo) {
  try
  {
    if(!feature){
      throw Error("Missing feature to calculate default funding acreage")
    }

    const featureObj = feature.attributes ?? feature;
    //This will be the JoinID field on anything that isn't a view and ID on anything that is a view
    const joinID = featureObj[FieldNames.featureFields.joinID] ? featureObj[FieldNames.featureFields.joinID] : featureObj[FieldNames.featureFields.id];
    const joinFeature = featureObj[FieldNames.featureFields.joinFeature] ? featureObj[FieldNames.featureFields.joinFeature] : FieldNames.joinFeatureValues.polygon;
    const calculatedArea = featureObj[FieldNames.featureFields.calculatedArea];
    const outFields = "OBJECTID, FundingSourceProgram, TotalCost, Acres";
    return FundingService.getFundingByJoinID(joinID, joinFeature, outFields).then(resp => {
      let fundingRows = [];
      resp.forEach(fundingItem => {
        const obj = {
          [FieldNames.featureFields.objectID]: fundingItem[FieldNames.featureFields.objectID],
          [FieldNames.fundingFields.fundingSourceProgram]: fundingItem[FieldNames.fundingFields.fundingSourceProgram],
          [FieldNames.fundingAcreageFields.acres]: fundingItem[FieldNames.fundingAcreageFields.acres],
          [FieldNames.fundingFields.totalCost]: fundingItem[FieldNames.fundingFields.totalCost],
        }
        fundingRows.push(obj);
      })

      let updatedRows = CalculateDefaultFundingAcres(fundingRows, calculatedArea);
      let relevantUpdates = updatedRows.map(row => {
        let rowObj = {}
        rowObj[FieldNames.featureFields.objectID] = row[FieldNames.featureFields.objectID];
        rowObj[FieldNames.fundingAcreageFields.acres] = row[FieldNames.fundingAcreageFields.acres];
        return rowObj;
      }) 
  
      return FundingService.updateFundingEntries(relevantUpdates).then((response) => {
        if (
          response &&
          response.updateResults &&
          response.updateResults.length > 0 &&
          response.updateResults[0].success
        ) {
          const featureUpdateObj = {
            [FieldNames.featureFields.objectID]: featureObj[FieldNames.featureFields.objectID],
            [FieldNames.featureFields.isFundingAcresManual]: FieldNames.yesNo.no
          }
          featureObj[FieldNames.featureFields.isFundingAcresManual] = FieldNames.yesNo.no;

          FeatureService.updateFeatureAttributes([featureUpdateObj]).then(response => {
            return updatedRows
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
            return updatedRows;
          });
          return updatedRows;
        } else {
          return [];
        }
      });
    });
  }
  catch(e)
  {
    return new Promise((resolve,reject) => {
      reject(e);
    });
  }
}

export function CalculateDefaultFundingAcres(fundingLines, calculatedArea) {
  if(!fundingLines || fundingLines?.length === 0)
  {
    return [];
  }
  let updatedFundingLines = [];
  const areAllRowsContributed = fundingLines?.length > 0 && 
                                  fundingLines.filter(x => x[FieldNames.fundingFields.fundingSourceProgram] 
                                    === FieldNames.fundingSourcePrograms.contributed).length === fundingLines?.length;

  // Right now this is only Fuels as an alternative, but for future-proofing, keep it as non-contributed
  let nonContributedFundingLines = fundingLines.filter(x => x[FieldNames.fundingFields.fundingSourceProgram] 
                                  !== FieldNames.fundingSourcePrograms.contributed &&
                                    x[FieldNames.fundingFields.totalCost] !== 0);
  let contributedFundingLines = fundingLines.filter(x => x[FieldNames.fundingFields.fundingSourceProgram] 
                                  === FieldNames.fundingSourcePrograms.contributed &&
                                    x[FieldNames.fundingFields.totalCost] !== 0);
  let zeroTotalCostLines = fundingLines.filter(x => x[FieldNames.fundingFields.totalCost] === 0);

  let totalCosts = 0;
  let rowsToUpdate = [];
  let rowsToEmpty = [];

  // Contributed lines should only be counted if all rows are contributed
  if(areAllRowsContributed)
  {
    rowsToUpdate = contributedFundingLines;
    rowsToEmpty = zeroTotalCostLines;
  }
  else
  {
    rowsToUpdate = nonContributedFundingLines;
    rowsToEmpty = zeroTotalCostLines.concat(contributedFundingLines);
  }

  // Calculate split costs for relevant rows
  totalCosts = rowsToUpdate.map(row => row[FieldNames.fundingFields.totalCost]).reduce((a, b) => a + b, 0);
  rowsToUpdate.map(row => {
    const rowCost = row[FieldNames.fundingFields.totalCost];
    if (!rowCost || !totalCosts) {
        return row;
    }
    const calculatedPercentage = rowCost / totalCosts;
    
    row[FieldNames.fundingAcreageFields.acres] = Math.round(calculatedArea * calculatedPercentage);
    return row;
  });

  rowsToEmpty.map(row => {
    row[FieldNames.fundingAcreageFields.acres] = 0;
    return row;
  })

  updatedFundingLines = updatedFundingLines.concat(rowsToUpdate);
  updatedFundingLines = updatedFundingLines.concat(rowsToEmpty);

  return updatedFundingLines;
}

// ---------------------------------------------------------------------------
// Internal helpers – small, pure functions that are individually testable.
// ---------------------------------------------------------------------------

/**
 * Collect unique, non-null values for a given field from all rows.
 *
 * @param {Array<Record<string,any>>} rows
 * @param {string}                    fieldName
 * @returns {string[]}  unique values in insertion order
 */
export function collectUniqueValues(rows, fieldName) {
    const seen = new Set();
    const result = [];
    for (const row of rows) {
        const value = row[fieldName];
        if (value != null && value !== '' && !seen.has(value)) {
            seen.add(value);
            result.push(value);
        }
    }
    return result;
}

/**
 * Join an array into a pipe-delimited string, or return null when empty.
 *
 * @param {string[]} values
 * @returns {string|null}
 */
export function joinPipeDelimited(values) {
    return values.length > 0 ? values.join('|') : null;
}

/**
 * Sum a numeric cost field across all rows.
 * Values are run through `removeCommas` to handle formatted strings.
 *
 * @param {Array<Record<string,any>>} rows
 * @param {string}                    fieldName
 * @returns {number}
 */
export function sumCostField(rows, fieldName) {
    return rows.reduce((total, row) => {
        const rawValue = row[fieldName];
        // Skip null/undefined/empty-string values
        if (rawValue === null || rawValue === undefined || rawValue === '') {
            return total;
        }
        // Normalize formatted strings like "1,000" to a numeric value
        const cleaned = removeCommas(String(rawValue));
        const numeric = Number(cleaned);
        return Number.isFinite(numeric) ? total + numeric : total;
    }, 0);
}

/**
 * Determine whether any row in the set has a BIL flag.
 * A row is BIL when its `IsBIL` field is "Yes" OR its `FundingSourceCategory`
 * contains the substring "bil" (case-insensitive).
 *
 * @param {Array<Record<string,any>>} rows
 * @returns {boolean}
 */
export function hasAnyBilRow(rows) {
    return rows.some(
        (row) =>
            row[FieldNames.fundingFields.isBIL] === FieldNames.yesNo.yes ||
            (row[FieldNames.fundingFields.fundingSourceCategory] &&
                row[FieldNames.fundingFields.fundingSourceCategory].toLowerCase().includes('bil'))
    );
}

/**
 * Determine whether any row in the set has a BAR flag.
 *
 * @param {Array<Record<string,any>>} rows
 * @returns {boolean}
 */
export function hasAnyBarRow(rows) {
    return rows.some(
        (row) =>
            row[FieldNames.fundingFields.isBAR] === FieldNames.yesNo.yes ||
            (row[FieldNames.fundingFields.fundingSourceCategory] &&
                row[FieldNames.fundingFields.fundingSourceCategory].toLowerCase().includes('bar'))
    );
}

/**
 * Calculate the total cost of rows that individually qualify as BIL.
 *
 * @param {Array<Record<string,any>>} rows
 * @returns {number}
 */
export function sumBilCost(rows) {
    return rows.reduce((total, row) => {
        const isBil =
            row[FieldNames.fundingFields.isBIL] === FieldNames.yesNo.yes ||
            (row[FieldNames.fundingFields.fundingSourceCategory] &&
                row[FieldNames.fundingFields.fundingSourceCategory].toLowerCase().includes('bil'));
        if (isBil && row[FieldNames.fundingFields.totalCost]) {
            return total + row[FieldNames.fundingFields.totalCost];
        }
        return total;
    }, 0);
}

/**
 * Derive updated WBS ID when the set of funding agencies changes.
 *
 * Rules:
 *  - If BLM was NOT in the old agencies but IS in the new agencies → set WBS to placeholder.
 *  - If BLM WAS in the old agencies but is NOT in the new agencies → strip BLM prefix/suffix.
 *  - Otherwise → leave unchanged.
 *
 * @param {string|null} currentWbsId
 * @param {string|null} oldAgencyString  – pipe-delimited agencies previously on the feature
 * @param {string|null} newAgencyString  – pipe-delimited agencies derived from current rows
 * @returns {string|null|undefined}  the new WBS value, or undefined when no change is needed
 */
export function deriveWbsId(currentWbsId, oldAgencyString, newAgencyString) {
    const hadBlm = oldAgencyString && oldAgencyString.includes(FieldNames.agencies.blm);
    const hasBlm = newAgencyString && newAgencyString.includes(FieldNames.agencies.blm);

    if (!hadBlm && hasBlm) {
        return 'XXXXXXXXXXXX';
    }
    if (hadBlm && !hasBlm && currentWbsId) {
        const prefixRemoved = currentWbsId.replace(FieldNames.wbsPrefixes.blm, '');
        return prefixRemoved.slice(0, -4);
    }
    return undefined; // no change
}

/**
 * Determine default IsRTRL value when agencies change.
 *
 * @param {any}         currentIsRtrl
 * @param {string|null} newAgencyString
 * @returns {string|undefined}  "No" if defaulting is needed, otherwise undefined
 */
export function deriveIsRtrl(currentIsRtrl, newAgencyString) {
    if (
        currentIsRtrl == null &&
        newAgencyString &&
        newAgencyString.includes(FieldNames.agencies.bia)
    ) {
        return FieldNames.yesNo.no;
    }
    return undefined;
}

/** Mapping from funding-row field → unique-values collector key */
const concatenatedFundingFieldMap = [
    { rowField: FieldNames.fundingFields.fundingDepartment,        parentField: FieldNames.featureFields.fundingDepartment },
    { rowField: FieldNames.fundingFields.fundingAgency,            parentField: FieldNames.featureFields.fundingAgency },
    { rowField: FieldNames.fundingFields.fundingRegion,            parentField: FieldNames.featureFields.fundingRegion },
    { rowField: FieldNames.fundingFields.fundingUnit,              parentField: FieldNames.featureFields.fundingUnit },
    { rowField: FieldNames.fundingFields.fundingSubUnit,           parentField: FieldNames.featureFields.fundingSubUnit },
    { rowField: FieldNames.fundingFields.fundingTribe,             parentField: FieldNames.featureFields.fundingTribe },
    { rowField: FieldNames.fundingFields.fundingUnitType,          parentField: FieldNames.featureFields.fundingUnitType },
    { rowField: FieldNames.fundingFields.fundingUnitID,            parentField: FieldNames.featureFields.fundingUnitID },
    { rowField: FieldNames.fundingFields.functionalArea,           parentField: FieldNames.featureFields.functionalArea },
    { rowField: FieldNames.fundingFields.costCenter,               parentField: FieldNames.featureFields.costCenter },
    { rowField: FieldNames.fundingFields.costCode,                 parentField: FieldNames.featureFields.costCode },
    { rowField: FieldNames.fundingFields.fundingSource,            parentField: FieldNames.featureFields.fundingSource },
    { rowField: FieldNames.fundingFields.fundingSourceProgram,     parentField: FieldNames.featureFields.fundingSourceProgram },
    { rowField: FieldNames.fundingFields.fundingSourceCategory,    parentField: FieldNames.featureFields.fundingSourceCategory },
    { rowField: FieldNames.fundingFields.fundingSourceSubCategory, parentField: FieldNames.featureFields.fundingSourceSubCategory },
];

/** Mapping from funding-row cost field → parent estimated-cost field */
const costFieldMap = [
    { rowField: FieldNames.fundingFields.assetCost,       parentField: FieldNames.featureFields.estimatedAssetCost },
    { rowField: FieldNames.fundingFields.contractualCost, parentField: FieldNames.featureFields.estimatedContractualCost },
    { rowField: FieldNames.fundingFields.grantsFixedCost, parentField: FieldNames.featureFields.estimatedGrantsFixedCost },
    { rowField: FieldNames.fundingFields.personnelCost,   parentField: FieldNames.featureFields.estimatedPersonnelCost },
    { rowField: FieldNames.fundingFields.otherCost,       parentField: FieldNames.featureFields.estimatedOtherCost },
    { rowField: FieldNames.fundingFields.totalCost,       parentField: FieldNames.featureFields.estimatedTotalCost },
];

// logic mostly from the FundingCostGrid saveFeatureFundingCostValues
export function deriveParentAttributesFromFundingRows(fundingRows, parentAttributes, userInfo) {
    const rows = fundingRows ?? [];
    const attrs = {};

    // concatenated fields using pipe delimiter
    for (const { rowField, parentField } of concatenatedFundingFieldMap) {
        attrs[parentField] = joinPipeDelimited(collectUniqueValues(rows, rowField));
    }

    // cost totals
    for (const { rowField, parentField } of costFieldMap) {
        attrs[parentField] = sumCostField(rows, rowField);
    }

    // BIL / BAR sums
    const isBil = rows.length > 0 && hasAnyBilRow(rows);
    const isBar = rows.length > 0 && hasAnyBarRow(rows);

    attrs[FieldNames.featureFields.isBIL] = isBil ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
    attrs[FieldNames.featureFields.isBAR] = isBar ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
    attrs[FieldNames.featureFields.bilFunding] = isBil ? sumBilCost(rows) : 0;

    // wbs id handling
    const oldAgency = parentAttributes?.[FieldNames.featureFields.fundingAgency] ?? null;
    const newAgency = attrs[FieldNames.featureFields.fundingAgency];
    const derivedWbs = deriveWbsId(
        parentAttributes?.[FieldNames.featureFields.wbsID],
        oldAgency,
        newAgency
    );
    if (derivedWbs !== undefined) {
        attrs[FieldNames.featureFields.wbsID] = derivedWbs;
    }

    // BIA isRTRL handling
    const derivedRtrl = deriveIsRtrl(
        parentAttributes?.[FieldNames.featureFields.isRTRL],
        newAgency
    );
    if (derivedRtrl !== undefined) {
        attrs[FieldNames.featureFields.isRTRL] = derivedRtrl;
    }

    attrs[FieldNames.featureFields.isFundingAcresManual] = FieldNames.yesNo.no;
    attrs[FieldNames.featureFields.modifiedBy] =
        userInfo?.[FieldNames.userFields.nifcUsername] ?? null;
    attrs[FieldNames.featureFields.lastModifiedDate] = new Date().getTime();

    return attrs;
}

export function isFundingCellEditable(params, editableFeature, areAllRowsContributed){
  return params?.field === FieldNames.fundingAcreageFields.acres &&
            (params?.row[FieldNames.fundingFields.fundingSourceProgram] !== FieldNames.fundingSourcePrograms.contributed
              || areAllRowsContributed) && (editableFeature != null && editableFeature?.attributes[FieldNames.featureFields.isPoint] !== FieldNames.yesNo.yes && params?.row[FieldNames.fundingFields.joinFeature] === FieldNames.joinFeatureValues.polygon)
}